import { Link } from 'react-router';
import { useLocation, useNavigate } from 'react-router';
import { useLocale, useTranslations } from '@/i18n/use-locale';
import { Bell, ChevronDown, Languages, LogOut, Settings } from 'lucide-react';
import { useState, useTransition, type ReactNode } from 'react';
import { useAdminSession } from '@/app/admin-session';
import { activeNavItem } from '@/components/admin/nav';
import { ModalShell } from '@/components/admin/modal-shell';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SearchInput } from '@/components/ui/input';
import { interactive } from '@/components/ui/interactive';
import { formatCount } from '@/lib/format';
import { cn } from '@/lib/utils';

export interface AdminNotification {
  id: string;
  title: string;
  body: string;
  /** Already formatted for display — the header does not own time formatting. */
  time: string;
  href: string;
}

export interface AdminHeaderProps {
  user: { name: string; initials: string; role: string };
  /**
   * Outstanding work this admin can act on, newest first. There is no read/unread state
   * in Phase 1 and none is faked: every item is a queue entry waiting on a human, so the
   * bell's count is "how many things need you" rather than "how many you have not
   * clicked". The `notifications` table and its read receipts land with the notification
   * centre in weeks 9–10 (ADR-010).
   */
  notifications?: readonly AdminNotification[];
  /** Hidden for an admin with no talents grant — a search that 403s is worse than none. */
  canSearchTalents?: boolean;
  /**
   * Rendered at the inline start of the bar — the mobile nav hamburger, which
   * only exists below `lg` where the fixed sidebar is hidden.
   */
  leading?: ReactNode;
}

/**
 * The sticky console header: page title and hint on the inline start, then search, the
 * notification bell and the account menu.
 *
 * Title and hint are derived from the shared `ADMIN_NAV` table rather than passed down
 * by each page, which is what keeps them in step with the highlighted sidebar item.
 *
 * Search submits to the talents list instead of filtering in place. The prototype
 * focused the field and switched view; a URL that carries the query is worth more here —
 * an admin can bookmark a search, and it is the shape server-side filtering will need
 * when the list is backed by Postgres (ADR-005).
 */
export function AdminHeader({
  user,
  notifications = [],
  canSearchTalents = true,
  leading,
}: AdminHeaderProps) {
  const t = useTranslations('admin');
  const { locale, setLocale } = useLocale();
  const nav = useTranslations('admin.nav');
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const [signOutOpen, setSignOutOpen] = useState(false);
  const { signOut } = useAdminSession();
  const [signingOut, startSignOut] = useTransition();

  const active = activeNavItem(pathname);
  const pending = notifications.length;

  return (
    <header className="sticky top-0 z-30 border-b border-conc-100 bg-white/90 backdrop-blur">
      <div className="flex items-center gap-4 px-6 py-3.5">
        {leading}
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-lg font-bold text-navy">
            {active ? nav(`items.${active.key}`) : t('title')}
          </h1>
          <p className="truncate text-[12.5px] text-conc-500">
            {active ? nav(`hints.${active.key}`) : t('hint')}
          </p>
        </div>

        {canSearchTalents ? (
          <form
            role="search"
            className="hidden w-64 lg:block"
            onSubmit={(event) => {
              event.preventDefault();
              const query = new FormData(event.currentTarget).get('q');
              const trimmed = typeof query === 'string' ? query.trim() : '';
              void navigate(trimmed ? `/talents?q=${encodeURIComponent(trimmed)}` : '/talents');
            }}
          >
            <label className="sr-only" htmlFor="admin-search">
              {t('header.searchLabel')}
            </label>
            <SearchInput id="admin-search" name="q" placeholder={t('header.searchPlaceholder')} />
          </form>
        ) : null}

        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label={t('header.notifications')}
            className={cn(
              interactive,
              'relative grid h-9 w-9 place-items-center rounded-lg text-conc-600 hover:bg-sideline hover:text-navy data-[state=open]:bg-sideline data-[state=open]:text-navy',
            )}
          >
            <Bell aria-hidden className="h-5 w-5" strokeWidth={1.9} />
            {pending > 0 && (
              <span className="num absolute -end-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-gold px-1 text-[10px] font-bold text-white">
                {formatCount(pending)}
              </span>
            )}
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-80">
            <DropdownMenuLabel className="border-b border-conc-100">
              {t('header.notifications')}
            </DropdownMenuLabel>

            {notifications.length === 0 ? (
              <p className="px-4 py-6 text-center text-[12.5px] text-conc-500">
                {t('header.noNotifications')}
              </p>
            ) : (
              <ul className="max-h-80 overflow-y-auto">
                {notifications.map((notification) => (
                  <li key={notification.id}>
                    <DropdownMenuItem asChild>
                      <Link
                        to={notification.href}
                        className="items-start border-b border-conc-50 py-3"
                      >
                        <span
                          aria-hidden
                          className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-pitch"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block text-[13px] font-semibold text-navy">
                            {notification.title}
                          </span>
                          <span className="block text-[12px] leading-relaxed font-normal text-conc-500">
                            {notification.body}
                          </span>
                          <span className="num mt-1 block text-[11px] font-normal text-conc-300">
                            {notification.time}
                          </span>
                        </span>
                      </Link>
                    </DropdownMenuItem>
                  </li>
                ))}
              </ul>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label={t('header.account')}
            className={cn(
              interactive,
              'flex items-center gap-2 rounded-lg py-1 ps-2 pe-1 hover:bg-sideline data-[state=open]:bg-sideline',
            )}
          >
            <span
              aria-hidden
              className="grid h-8 w-8 place-items-center rounded-full bg-navy text-[11px] font-bold text-white"
            >
              {user.initials}
            </span>
            <ChevronDown aria-hidden className="h-4 w-4 text-conc-300" />
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-56 py-1">
            <div className="border-b border-conc-50 px-4 py-2.5 leading-tight">
              <p className="truncate text-[13px] font-bold text-navy">{user.name}</p>
              <p className="truncate text-[11.5px] text-conc-500">{user.role}</p>
            </div>
            <DropdownMenuItem asChild>
              <Link to="/settings">
                <Settings aria-hidden />
                {t('header.settings')}
              </Link>
            </DropdownMenuItem>
            {/* The other language, named in itself, so it is readable whichever one is on. */}
            <DropdownMenuItem
              onSelect={() => setLocale(locale === 'ar' ? 'en' : 'ar')}
              aria-label={t('header.switchLanguage')}
            >
              <Languages aria-hidden />
              {locale === 'ar' ? t('header.languageEnglish') : t('header.languageArabic')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="danger" onSelect={() => setSignOutOpen(true)}>
              <LogOut aria-hidden />
              {t('header.signOut')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/*
        Sign-out is confirmed, exactly as the prototype has it. Not ceremony: the console
        is opened once and left open for a shift, and an accidental sign-out costs a
        password, a phone and a fresh six digits (ADR-007).
      */}
      <ModalShell
        open={signOutOpen}
        onOpenChange={setSignOutOpen}
        title={t('signOut.title')}
        description={t('signOut.hint')}
        closeLabel={t('actions.close')}
        className="max-w-sm"
        footer={
          <>
            <Button
              variant="danger"
              className="flex-1"
              disabled={signingOut}
              onClick={() => startSignOut(() => void signOut())}
            >
              {signingOut ? t('signOut.pending') : t('header.signOut')}
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              disabled={signingOut}
              onClick={() => setSignOutOpen(false)}
            >
              {t('actions.cancel')}
            </Button>
          </>
        }
      >
        <p className="text-[13px] leading-relaxed text-conc-500">{t('signOut.body')}</p>
      </ModalShell>
    </header>
  );
}
